import cloneDeep from 'lodash/cloneDeep'

export const SEA_BOOKING_COPY_CONFIG_MODULE = 11000
export const SEA_BOOKING_COPY_OPTIONS_CACHE_PREFIX = 'SEA_BOOKING_COPY_OPTIONS_'

export const SEA_BOOKING_COPY_BASIC_ITEM_KEYS = ['basicInfo', 'booking', 'prePlanBooking', 'mbl', 'hbl', 'charge']

function toBoolean(value) {
  return value === true || value === 1 || value === '1'
}

function normalizeFieldConfigItem(item = {}) {
  return {
    itemKey: item.itemKey || '',
    itemLabel: item.itemLabel || item.itemKey || '',
    defaultSelected: toBoolean(item.defaultSelected),
    fieldList: Array.isArray(item.fieldList) ? item.fieldList.slice() : []
  }
}

function parseConfigContent(configContent, fallback) {
  if (!configContent) {
    return fallback
  }
  if (typeof configContent === 'object') {
    return configContent
  }
  try {
    return JSON.parse(configContent)
  } catch (err) {
    return fallback
  }
}

export function parseGlobalCopyFieldConfig(configContent) {
  const parsed = parseConfigContent(configContent, [])
  return Array.isArray(parsed) ? parsed.map(normalizeFieldConfigItem).filter(item => item.itemKey) : []
}

export function createDefaultSeaBookingCopyOptions(copyFieldConfig = []) {
  return {
    copyNum: 1,
    notMatchAgainSelect: false,
    copyFieldConfig: copyFieldConfig.map(normalizeFieldConfigItem).filter(item => item.itemKey)
  }
}

export function normalizeSchemeContent(content = {}, globalCopyFieldConfig = []) {
  const nextContent = content || {}
  const selectedMap = new Map((nextContent.copyFieldConfig || []).map(item => [item.itemKey, item]))
  const copyFieldConfig = globalCopyFieldConfig.map(globalItem => {
    const selectedItem = selectedMap.get(globalItem.itemKey)
    return {
      ...normalizeFieldConfigItem(globalItem),
      defaultSelected: selectedItem ? toBoolean(selectedItem.defaultSelected) : toBoolean(globalItem.defaultSelected)
    }
  })
  return {
    copyNum: Number(nextContent.copyNum) > 0 ? Number(nextContent.copyNum) : 1,
    notMatchAgainSelect: Boolean(nextContent.notMatchAgainSelect),
    copyFieldConfig
  }
}

export function createDefaultScheme(copyFieldConfig = []) {
  return {
    id: null,
    bDefault: false,
    builtin: true,
    configName: 'default',
    content: createDefaultSeaBookingCopyOptions(copyFieldConfig)
  }
}

export function parsePersonalScheme(item = {}, globalCopyFieldConfig = []) {
  const content = parseConfigContent(item.configContent, {})
  return {
    ...item,
    bDefault: toBoolean(item.bDefault),
    builtin: false,
    content: normalizeSchemeContent(content, globalCopyFieldConfig)
  }
}

export function resolveInitialScheme(personalSchemes = [], builtinScheme = createDefaultScheme(), globalCopyFieldConfig = []) {
  const parsed = personalSchemes.map(item => parsePersonalScheme(item, globalCopyFieldConfig))
  return parsed.find(item => toBoolean(item.bDefault)) || builtinScheme
}

export function isCopyFieldSelected(content = {}, itemKey) {
  const normalized = normalizeSchemeContent(content, content.copyFieldConfig || [])
  const item = normalized.copyFieldConfig.find(fieldConfig => fieldConfig.itemKey === itemKey)
  return item ? item.defaultSelected === true : false
}

export function resolveLegacyVesselEqSelected(legacyValue, options = {}) {
  if (typeof legacyValue !== 'undefined' && legacyValue !== null && legacyValue !== '') {
    return legacyValue
  }
  return isCopyFieldSelected(options, 'cargoInfo')
}

export function setCopyFieldSelected(content = {}, itemKey, selected) {
  return {
    ...content,
    copyFieldConfig: (content.copyFieldConfig || []).map(item => {
      if (item.itemKey !== itemKey) {
        return item
      }
      return {
        ...item,
        defaultSelected: selected
      }
    })
  }
}

export function getSelectedCopyFieldList(content = {}) {
  const fieldSet = new Set()
  const copyFieldConfig = content.copyFieldConfig || []
  copyFieldConfig.forEach(item => {
    if (!toBoolean(item.defaultSelected)) {
      return
    }
    const fieldList = item.fieldList || []
    fieldList.forEach(field => fieldSet.add(field))
  })
  return [...fieldSet]
}

function shouldKeepWholeCopiedJob(content = {}, itemKey) {
  const copyFieldConfig = content.copyFieldConfig || []
  const item = copyFieldConfig.find(fieldConfig => fieldConfig.itemKey === itemKey)
  if (!item || !toBoolean(item.defaultSelected)) {
    return false
  }
  return !Array.isArray(item.fieldList) || item.fieldList.length === 0
}

function filterSeaCargoDocs(cargoDocs, options = {}) {
  if (!Array.isArray(cargoDocs)) {
    return cargoDocs
  }
  const keepMbl = isCopyFieldSelected(options, 'mbl')
  const keepHbl = isCopyFieldSelected(options, 'hbl')
  if (keepMbl && keepHbl) {
    return cargoDocs
  }
  if (!keepMbl && !keepHbl) {
    return []
  }
  const mblDocTypes = ['MBL', 'MBL2']
  const hblDocTypes = ['HBL', 'HBL2']
  return cargoDocs.filter(doc => {
    if (!doc || !doc.docType) {
      return false
    }
    if (keepMbl && mblDocTypes.includes(doc.docType)) {
      return true
    }
    if (keepHbl && hblDocTypes.includes(doc.docType)) {
      return true
    }
    return false
  })
}

function clearBasicTabPayloads(target = {}, options = {}) {
  target.signMBL = isCopyFieldSelected(options, 'mbl')
  target.signHBL = isCopyFieldSelected(options, 'hbl')
  if (!isCopyFieldSelected(options, 'prePlanBooking')) {
    target.manifestInfos = []
    target.trucking = []
  }
  if (!isCopyFieldSelected(options, 'charge')) {
    target.charges = []
  }
  const filteredSeaCargoDoc = filterSeaCargoDocs(target.seaCargoDoc, options)
  if (Array.isArray(target.seaCargoDoc)) {
    target.seaCargoDoc = filteredSeaCargoDoc
  }
  const filteredSeaCargoDocs = filterSeaCargoDocs(target.seaCargoDocs, options)
  if (Array.isArray(target.seaCargoDocs)) {
    target.seaCargoDocs = filteredSeaCargoDocs
  }
}

function mergeBasicTabPayloads(prepared = {}, source = {}, options = {}) {
  if (shouldKeepWholeCopiedJob(options, 'mbl')) {
    prepared.signMBL = true
  }
  if (shouldKeepWholeCopiedJob(options, 'hbl')) {
    prepared.signHBL = true
  }
  if (shouldKeepWholeCopiedJob(options, 'prePlanBooking')) {
    copyOwnFields(source, prepared, ['manifestInfos', 'trucking'])
  }
  if (shouldKeepWholeCopiedJob(options, 'charge')) {
    copyOwnFields(source, prepared, ['charges'])
  }
  if (shouldKeepWholeCopiedJob(options, 'mbl') || shouldKeepWholeCopiedJob(options, 'hbl')) {
    if (Array.isArray(source.seaCargoDoc)) {
      prepared.seaCargoDoc = filterSeaCargoDocs(cloneDeep(source.seaCargoDoc), options)
    }
    if (Array.isArray(source.seaCargoDocs)) {
      prepared.seaCargoDocs = filterSeaCargoDocs(cloneDeep(source.seaCargoDocs), options)
    }
  }
}

export function applyExplicitCopyFieldsFromSource(source = {}, target = {}, options = {}) {
  const nextTarget = cloneDeep(target || {})
  copyOwnFields(source || {}, nextTarget, getSelectedCopyFieldList(options))
  // 箱子相关数据以后端 target(doCopyJob 返回的 newJob)为准：
  // 后端带了的保留；后端没带的(典型 containers=null)必须清空，不能留内存源 source 带进来的旧
  // containers——否则 containers 与 containerUsages 不一致，且 onShow 的 autoJustContainerUsages
  // 会因"箱子数已满(eqQuantity)"而不补 containerUsages，导致集装箱 tab 箱货信息空白。
  // 复制统一由 autoJustContainerUsages 按 seaEQ 生成空壳(见 bookingWorkspace.vue onShow)。
  if (target && Array.isArray(target.containers)) {
    nextTarget.containers = cloneDeep(target.containers)
  } else {
    delete nextTarget.containers
  }
  if (target && Array.isArray(target.seaEQ)) {
    nextTarget.seaEQ = cloneDeep(target.seaEQ)
  }
  // 顶层 containerUsages 始终走 containers[].containerUsage 嵌套路径
  delete nextTarget.containerUsages
  return nextTarget
}

export function shouldPreserveCopiedCarryingCmp(job = {}, action = '') {
  return action === 'copy' &&
    !job.uuid &&
    Number(job.carryingCmp) > 0 &&
    (!Array.isArray(job.trucking) || job.trucking.length === 0)
}

export function shouldPreserveCopiedWarehouseCmp(job = {}, action = '') {
  return action === 'copy' &&
    !job.uuid &&
    Number(job.warehouseCmp) > 0 &&
    (!Array.isArray(job.warehouseInfo) || job.warehouseInfo.length === 0)
}

function copyOwnFields(source, target, fieldNames) {
  fieldNames.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      target[key] = cloneDeep(source[key])
    }
  })
}

function clearAddressBlock(target, prefix) {
  const idKey = `${prefix}Id`
  const codeKey = `${prefix}Code`
  if (Object.prototype.hasOwnProperty.call(target, idKey)) {
    target[idKey] = 0
  }
  if (Object.prototype.hasOwnProperty.call(target, codeKey)) {
    target[codeKey] = ''
  }
  for (let i = 1; i <= 7; i++) {
    const lineKey = `${prefix}Line${i}`
    if (Object.prototype.hasOwnProperty.call(target, lineKey)) {
      target[lineKey] = ''
    }
  }
}

function cleanupCargoDocs(cargoDocs, options) {
  if (!Array.isArray(cargoDocs)) {
    return cargoDocs
  }
  return cargoDocs.map(doc => {
    const nextDoc = cloneDeep(doc)
    if (!isCopyFieldSelected(options, 'shipper')) {
      clearAddressBlock(nextDoc, 'shipper')
    }
    if (!isCopyFieldSelected(options, 'consignee')) {
      clearAddressBlock(nextDoc, 'consignee')
    }
    if (!isCopyFieldSelected(options, 'notify')) {
      clearAddressBlock(nextDoc, 'notify')
    }
    if (!isCopyFieldSelected(options, 'cargoEnglishName')) {
      nextDoc.cargoEnglishName = ''
    }
    if (!isCopyFieldSelected(options, 'mark')) {
      nextDoc.mark = ''
    }
    return nextDoc
  })
}

export function cleanupSeaBookingCopiedJob(job, options = {}) {
  const cleaned = cloneDeep(job || {})
  if (Array.isArray(cleaned.seaCargoDoc)) {
    cleaned.seaCargoDoc = cleanupCargoDocs(cleaned.seaCargoDoc, options)
  }
  if (Array.isArray(cleaned.seaCargoDocs)) {
    cleaned.seaCargoDocs = cleanupCargoDocs(cleaned.seaCargoDocs, options)
  }
  return cleaned
}

export function prepareSeaBookingCopiedJobForDetail(job, options = {}) {
  const source = cleanupSeaBookingCopiedJob(job, options)
  const prepared = shouldKeepWholeCopiedJob(options, 'booking')
    ? cloneDeep(source)
    : applyExplicitCopyFieldsFromSource(source, {}, options)
  if (shouldKeepWholeCopiedJob(options, 'booking')) {
    clearBasicTabPayloads(prepared, options)
  } else {
    mergeBasicTabPayloads(prepared, source, options)
  }
  // 复制方案只控制业务字段，以下是页面初始化/下拉联动所需的上下文字段，需始终保留
  copyOwnFields(source, prepared, ['lob', 'containerLoadType', 'fba', 'masterFlag', 'copyNum'])
  // 箱型箱量勾选时，才保留 seaEQ/containers/containerUsages
  if (isCopyFieldSelected(options, 'cargoInfo')) {
    copyOwnFields(source, prepared, ['seaEQ', 'containers', 'containerUsages'])
    // 清空 container 的 uuid 和 containerUsage 的 uuid/containerUuid，
    // 让后端走 INSERT 路径，避免 FK 外键约束失败
    cleanupCopiedContainerUuids(prepared)
  }
  return prepared
}

function isBodyFieldEmpty(body, field) {
  return !Object.prototype.hasOwnProperty.call(body, field) ||
    !Array.isArray(body[field]) ||
    body[field].length === 0
}

export function mergeCopyUntouchedTabData(body = {}, job = {}, action = '') {
  if (!body || !job || (action !== 'copy' && action !== 'split')) {
    return body
  }
  if (isBodyFieldEmpty(body, 'seaEQ') && Array.isArray(job.seaEQ) && job.seaEQ.length > 0) {
    body.seaEQ = cloneDeep(job.seaEQ)
  }
  if (isBodyFieldEmpty(body, 'containers') && Array.isArray(job.containers) && job.containers.length > 0) {
    body.containers = cloneDeep(job.containers)
  }
  // copy 时不克隆顶层 containerUsages：containers 的 uuid 全部为空（尚未 INSERT），
  // 后端先保存 containerUsages 再保存 containers，会导致 FK 外键约束失败。
  // 装箱数据通过 containers[].containerUsage 嵌套路径保存是安全的。
  // split 时保留原有行为，因为 split 的 container 可能有已存在的 uuid。
  if (action !== 'copy' && !Object.prototype.hasOwnProperty.call(body, 'containerUsages') && Array.isArray(job.containerUsages) && job.containerUsages.length > 0) {
    body.containerUsages = cloneDeep(job.containerUsages)
  }
  // 清空 container/containerUsage 的 uuid，避免后端 FK 外键约束失败
  cleanupCopiedContainerUuids(body)
  if (!Object.prototype.hasOwnProperty.call(body, 'cargoBulkDetailList') && Array.isArray(job.cargoBulkDetail) && job.cargoBulkDetail.length > 0) {
    body.cargoBulkDetailList = cloneDeep(job.cargoBulkDetail)
  }
  return body
}

/**
 * 清空复制数据中 container 和 containerUsage 的 uuid，
 * 确保后端走 INSERT 路径而非 UPDATE，避免外键约束失败。
 *
 * 后端 saveContainer 逻辑：
 *   - container.uuid 为空 → INSERT（数据库生成新 uuid）→ 再用新 uuid 关联 containerUsage
 *   - container.uuid 不为空 → selectByPrimaryKey → 找不到就跳过 → containerUsage FK 报错
 *
 * 同时清空 containerUsage.containerUuid，让后端通过 container.containerUsage 嵌套关系重新设置。
 */
export function cleanupCopiedContainerUuids(prepared = {}) {
  if (Array.isArray(prepared.containers)) {
    for (const container of prepared.containers) {
      container.uuid = ''
      if (Array.isArray(container.containerUsage)) {
        for (const usage of container.containerUsage) {
          usage.uuid = ''
          usage.containerUuid = ''
        }
      }
    }
  }
  if (Array.isArray(prepared.containerUsages)) {
    for (const usage of prepared.containerUsages) {
      usage.uuid = ''
      usage.containerUuid = ''
    }
  }
}
